import React, { createContext, useContext, useMemo, useReducer } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────

type PublicFlowState = {
  formValues: Record<string, string>;
  suitabilityBlockers: Record<string, boolean>;
};

type PublicFlowAction =
  | { type: "SET_FIELD_VALUE"; fieldId: string; value: string }
  | { type: "SET_FORM_VALUES"; values: Record<string, string> }
  | { type: "SET_SUITABILITY_BLOCKER"; goalId: string; hasBlocker: boolean };

// ─── Reducer ─────────────────────────────────────────────────────────────────

function publicFlowReducer(state: PublicFlowState, action: PublicFlowAction): PublicFlowState {
  switch (action.type) {
    case "SET_FIELD_VALUE":
      return {
        ...state,
        formValues: { ...state.formValues, [action.fieldId]: action.value },
      };
    case "SET_FORM_VALUES":
      return {
        ...state,
        formValues: { ...state.formValues, ...action.values },
      };
    case "SET_SUITABILITY_BLOCKER":
      return {
        ...state,
        suitabilityBlockers: {
          ...state.suitabilityBlockers,
          [action.goalId]: action.hasBlocker,
        },
      };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

const PublicFlowStateContext = createContext<PublicFlowState | null>(null);
const PublicFlowDispatchContext = createContext<React.Dispatch<PublicFlowAction> | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

type PublicFlowProviderProps = {
  children: React.ReactNode;
};

export function PublicFlowProvider({ children }: PublicFlowProviderProps) {
  // `formValues` starts empty and is populated reactively. Two sources merge
  // into the conditional-visibility view in `public-flow-content-inner.tsx`:
  //   1) Saved values + derived virtual fields come from dedicated React Query
  //      hooks (`usePublicFieldValues`, `usePublicClientClassificationValue`,
  //      `usePublicSumsubVerificationValue`) via a `useMemo`. Sourcing this
  //      eagerly from the bundled `/public/flow/instance` response is the
  //      old (pre-cleanup) pattern that's been removed.
  //   2) Optimistic in-progress values from renderers' `setFieldValue` calls
  //      live in the reducer's `formValues` map. The orchestrator's `useMemo`
  //      merges them LAST so a keystroke beats the older server snapshot
  //      during the brief save → invalidate window.
  const [state, dispatch] = useReducer(publicFlowReducer, {
    formValues: {},
    suitabilityBlockers: {},
  });

  return (
    <PublicFlowStateContext.Provider value={state}>
      <PublicFlowDispatchContext.Provider value={dispatch}>
        {children}
      </PublicFlowDispatchContext.Provider>
    </PublicFlowStateContext.Provider>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

function usePublicFlowState(): PublicFlowState {
  const state = useContext(PublicFlowStateContext);
  if (!state) throw new Error("usePublicFlowState must be used within PublicFlowProvider");
  return state;
}

function usePublicFlowDispatch(): React.Dispatch<PublicFlowAction> {
  const dispatch = useContext(PublicFlowDispatchContext);
  if (!dispatch) throw new Error("usePublicFlowDispatch must be used within PublicFlowProvider");
  return dispatch;
}

export function usePublicFormValues(): Record<string, string> {
  return usePublicFlowState().formValues;
}

export function usePublicHasSuitabilityBlockers(): boolean {
  const { suitabilityBlockers } = usePublicFlowState();
  return useMemo(() => Object.values(suitabilityBlockers).some((b) => b), [suitabilityBlockers]);
}

/**
 * Convenience hook that provides action-like helpers for the public flow context.
 */
export function usePublicFlowActions() {
  const dispatch = usePublicFlowDispatch();

  return useMemo(
    () => ({
      setFieldValue: (fieldId: string, value: string) =>
        dispatch({ type: "SET_FIELD_VALUE", fieldId, value }),
      setSuitabilityBlocker: (goalId: string, hasBlocker: boolean) =>
        dispatch({ type: "SET_SUITABILITY_BLOCKER", goalId, hasBlocker }),
    }),
    [dispatch],
  );
}
